import Alpine from "alpinejs";
import collapse from "@alpinejs/collapse";
import Swiper from 'swiper';
import { Autoplay, Navigation } from 'swiper/modules';

Alpine.plugin(collapse);

// Accordion component
Alpine.data("accordion", (defaultOpen = false) => ({
  open: defaultOpen,

  toggle() {
    this.open = !this.open;
  },

  // Helper to get chevron rotation class
  get chevronClass() {
    return this.open ? "rotate-180" : "";
  },

  // Common content transition attributes (can be overridden)
  contentTransition: {
    "x-show": "open",
    "x-collapse": true,
    "x-transition:enter": "transition ease-out duration-200",
    "x-transition:enter-start": "opacity-0 -translate-y-2",
    "x-transition:enter-end": "opacity-100 translate-y-0",
  },
}));

// Marquee component: auto-scrolls a row only when its items are wider than the row itself
Alpine.data("marquee", (speed = 40) => ({
  scrolling: false,
  reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  clone: null,

  get animating() {
    return this.scrolling && !this.reducedMotion;
  },

  init() {
    this.observer = new ResizeObserver(() => this.measure());
    this.observer.observe(this.$el);
  },

  destroy() {
    this.observer.disconnect();
  },

  measure() {
    const set = this.$refs.set;

    // Comparing against the row (not the content column) guarantees one set always
    // covers the visible width, so the loop copy is never on screen at the same time
    this.scrolling = set.offsetWidth > this.$el.clientWidth;

    // A second copy of the set makes the -50% loop seamless
    if (this.animating && !this.clone) {
      this.clone = set.cloneNode(true);
      this.clone.removeAttribute("x-ref");
      this.clone.setAttribute("aria-hidden", "true");
      this.clone.inert = true;
      set.after(this.clone);
    } else if (!this.animating && this.clone) {
      this.clone.remove();
      this.clone = null;
    }

    // Constant px/sec no matter how many items there are
    this.$refs.track.style.setProperty("--marquee-duration", `${set.offsetWidth / speed}s`);
  },
}));

// Make Alpine available globally
window.Alpine = Alpine;

// Start Alpine
Alpine.start();

// Initialize Swiper for testimonials
document.addEventListener('DOMContentLoaded', () => {
  const testimonialsSwiper = new Swiper('.testimonials-swiper', {
    modules: [Navigation],
    slidesPerView: 1,
    loop: true,
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
    keyboard: {
      enabled: true,
      onlyInViewport: true,
    },
    a11y: {
      prevSlideMessage: 'Previous testimonial',
      nextSlideMessage: 'Next testimonial',
      firstSlideMessage: 'This is the first testimonial',
      lastSlideMessage: 'This is the last testimonial',
    },
  });

  // Awards carousel (featured layouts): a static centered row when the cards fit the
  // viewport, otherwise a centered loop that auto-slides
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('.awards-swiper').forEach((el) => {
    const gap = 32;
    const wrapper = el.querySelector('.swiper-wrapper');
    const originals = [...wrapper.children];
    let swiper = null;
    let looping = null;

    const build = () => {
      const rowWidth = originals.length * (originals[0].offsetWidth + gap) - gap;
      const shouldLoop = rowWidth > el.clientWidth;
      if (shouldLoop === looping) return;
      looping = shouldLoop;

      swiper?.destroy(true, true);
      wrapper.replaceChildren(...originals);

      // Loop mode needs more slides than fit on screen, so append copies of the set
      if (looping) {
        const copies = Math.max(1, Math.ceil(8 / originals.length) - 1);
        for (let i = 0; i < copies; i++) {
          originals.forEach((slide) => {
            const clone = slide.cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            clone.querySelectorAll('a').forEach((link) => (link.tabIndex = -1));
            wrapper.append(clone);
          });
        }
      }

      swiper = new Swiper(el, {
        modules: [Navigation, Autoplay],
        slidesPerView: 'auto',
        spaceBetween: gap,
        speed: 600,
        loop: looping,
        centeredSlides: looping,
        centerInsufficientSlides: true,
        grabCursor: true,
        autoplay: looping && !reducedMotion ? {
          delay: 3000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        } : false,
        // Arrows sit outside the swiper, so look them up per block
        navigation: {
          prevEl: el.parentElement.querySelector('.awards-swiper-prev'),
          nextEl: el.parentElement.querySelector('.awards-swiper-next'),
        },
      });
    };

    new ResizeObserver(build).observe(el);
  });
});
